// @ts-strict-ignore
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import {purgeFetchCachePathsMatchingRegex} from 'common/report/queries/utils/fetch-cache';
import {apiClient} from 'common/util/api-client';
import {GlobalAccessParamName} from 'common/report/util/saved-entities/types';
import {ContentDetails} from 'common/report/util/dashboards/utils';
import {FetchOptions, SuccessResponse} from 'common/util/shared-client';
import {BlueprintConfig, ContentActionType, ContentType, Dashboard} from 'common/report/dashboards/types';
import {BaseQueryErrorType} from 'common/report/types';
import {
  addNewCellToRow,
  getLayoutParamWithContentRemoved,
  getLayoutUpdateParams,
  MAX_CELL_COL_SPAN,
} from 'common/report/util/dashboards/grid-layout';
import {DashboardDigest} from 'common/query-builder/query-report-header/subscribe-to-boards-menu/types';

import {appApiUrl} from '../utils';
import {BaseClient, QueryOptions} from '../utils/base-client';
import {NamedError} from '../utils/named-error';
import {DashboardCardWithAction} from './dashboard-types';

class FieldStringTooLongError extends NamedError {
  constructor(
    message,
    public fieldName: string,
    public fieldLength: string,
    public maxLength: string,
  ) {
    super(message);
  }

  static get type() {
    return `FieldStringTooLong`;
  }
}

class PermissionDeniedError extends NamedError {
  static get type() {
    return `PermissionDenied`;
  }
}

class NotImplementedError extends NamedError {
  static get type() {
    return `NotImplemented`;
  }
}

class InvalidParamsError extends NamedError {
  static get type() {
    return `InvalidParams`;
  }
}

class DashboardDoesNotExistError extends NamedError {
  static get type() {
    return `DashboardDoesNotExist`;
  }
}

class CardDoesNotExistError extends NamedError {
  static get type() {
    return `CardDoesNotExist`;
  }
}

class CardOrderOutOfSyncError extends NamedError {
  static get type() {
    return `CardOrderOutOfSync`;
  }
}

class BookmarkDoesNotExistError extends NamedError {
  static get type() {
    return `BookmarkDoesNotExist`;
  }
}

type ProjectId = string | number;

// TODO: refactor to use use xId rather than xID
interface BuildEndpointParams {
  projectId?: ProjectId;
  dashboardID?: number;
  bookmarkID?: number;
  erf?: boolean;
  emailDigest?: boolean;
  workspaceId?: number;
  favorites?: boolean;
  pin?: boolean;
  reportLinkId?: string;
}

export interface DashboardFields extends Partial<Pick<Dashboard, `title` | `description` | `filters` | `time_filter`>> {
  cards?: DashboardCardWithAction[];
}

export interface RCASourceData {
  timestamp?: string;
  type?: string;
  date?: string;
}

const VALID_DASHBOARD_PARAMS = [
  `title`,
  `description`,
  `time_filter`,
  `filters`,
  `card_order`,
  `cards`,
  `duplicate`,
  `target_project_id`,
  `target_parent_dashboard_id`,
  GlobalAccessParamName,
];

/* Dashboards 2.0 API is documented here: https://github.com/mixpanel/analytics/blob/master/app_api/projects/dashboards/README.md */
class DashClient extends BaseClient {
  arrayFormatBracket = true;

  buildEndpoint({
    projectId,
    dashboardID,
    bookmarkID,
    erf,
    emailDigest,
    workspaceId,
    favorites,
    pin,
    reportLinkId,
  }: BuildEndpointParams) {
    let endpoint;
    if (dashboardID) {
      endpoint = `dashboards/${dashboardID}/`;
      if (erf) {
        endpoint += `erf`;
      } else if (favorites) {
        endpoint += `favorites/`;
      } else if (pin) {
        endpoint += `pin/`;
      } else if (reportLinkId) {
        endpoint += `report-links/${reportLinkId}`;
      } else {
        endpoint += ``;
      }
    } else if (bookmarkID) {
      endpoint = `dashboards/bookmarks/${bookmarkID}/dashboard-ids/`;
    } else if (emailDigest) {
      endpoint = `dashboard-reports/email-digests`;
    } else {
      endpoint = `dashboards/`;
    }
    return appApiUrl(endpoint, {projectId, workspaceId});
  }

  buildQueryParams(params) {
    const dashboardIds = params.dashboardIds ?? [];
    return {id: dashboardIds};
  }

  getExtraFetchOptions() {
    return {};
  }

  getFetchOptions(params: any, queryOpts: QueryOptions): FetchOptions {
    return super.getFetchOptions(params, {...queryOpts, ...this.getExtraFetchOptions()});
  }

  /** Sends a HTTP GET request to retrieve all dashboards for a specified project */
  queryAll(
    {
      projectId,
      dashboardIds,
      workspaceId,
    }: {
      projectId?: ProjectId;
      dashboardIds?: number[];
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): {cached: Promise<Dashboard[] | null>; network: Promise<Dashboard[]>} {
    const params = {projectId, workspaceId, dashboardIds};
    return {
      cached: this.query(params, {...queryOpts, fromFetchCache: true}),
      network: this.query(params, {...queryOpts, updateFetchCache: true}),
    };
  }

  /** Sends a HTTP GET request to retrieve a specific dashboard */
  querySingle(
    {
      projectId,
      dashboardID,
      workspaceId,
    }: {
      projectId: ProjectId;
      dashboardID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): {cached: Promise<Dashboard | null>; network: Promise<Dashboard>} {
    if (!projectId || !dashboardID) {
      throw new Error(`projectId and dashboardID are required`);
    }

    const params = {projectId, dashboardID, workspaceId};
    return {
      cached: this.query(params, {...queryOpts, fromFetchCache: true}),
      network: this.query(params, {...queryOpts, updateFetchCache: true}),
    };
  }

  /** Sends a HTTP GET request to retrieve a specific dashboard for ERF */
  querySingleERF(
    {
      projectId,
      dashboardID,
      workspaceId,
    }: {
      projectId: ProjectId;
      dashboardID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): Promise<any> {
    return this.query({projectId, dashboardID, workspaceId, erf: true}, queryOpts);
  }

  queryEmailDigests(
    {projectId, workspaceId}: {projectId: ProjectId; workspaceId?: number},
    queryOpts?: QueryOptions,
  ): Promise<DashboardDigest[]> {
    return this.query({projectId, workspaceId, emailDigest: true}, queryOpts);
  }

  /** Sends a HTTP GET request to retrieve dashboards containing cards cards with target bookmark */
  queryBookmarkDashboardIds(
    {
      projectId,
      bookmarkID,
      workspaceId,
    }: {
      projectId: ProjectId;
      bookmarkID: number;
      workspaceId?: number;
    },
    queryOpts?: QueryOptions,
  ): Promise<number[]> {
    return this.query({projectId, bookmarkID, workspaceId}, queryOpts);
  }

  /** Sends a HTTP PATCH request to update the fields of a dashboard */
  async updateDashboardFields({
    projectId,
    dashboardID,
    fields = {},
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    fields: TODO;
    workspaceId?: number;
  }): Promise<any> {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.patch(url, fields, fetchOptions)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to create a new dashboard */
  async postCreateDashboard({
    projectId,
    fields = {},
    workspaceId,
  }: {
    projectId: ProjectId;
    fields: DashboardFields;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const postData: Partial<DashboardFields> = pick(fields, VALID_DASHBOARD_PARAMS);
    const url = this.buildEndpoint({projectId, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.post(url, postData, fetchOptions)));
    await this.invalidateFetchCache();
    return result;
  }
  /** Sends a HTTP POST request to create a root cause analaysis dashboard */
  async postCreateRCADashboard({
    projectId,
    rcaSourceId,
    rcaSourceData,
    workspaceId,
  }: {
    projectId: ProjectId;
    rcaSourceId: number;
    rcaSourceData: RCASourceData;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const url = `${this.buildEndpoint({workspaceId, projectId})}create-rca-dashboard/`;

    const result = await this.processResponse(
      await this.wrapErrorThrower(
        apiClient.post(url, {
          rca_source_id: rcaSourceId,
          rca_source_data: rcaSourceData,
        }),
      ),
    );
    await this.invalidateFetchCache();
    return result.dashboard;
  }

  async postCreateFoundationalTemplateDashboard({
    projectId,
    fields,
    templateType,
    workspaceId,
  }: {
    projectId: ProjectId;
    workspaceId: number;
    templateType: string;
    fields: Obj;
  }): Promise<Dashboard | BaseQueryErrorType> {
    const url = `${this.buildEndpoint({workspaceId, projectId})}foundational-templates/`;
    const fetchOptions = this.getExtraFetchOptions();

    try {
      const result = await this.processResponse(
        await this.wrapErrorThrower(apiClient.post(url, {fields, template_type: templateType}, fetchOptions)),
      );
      return result.dashboard;
    } catch (err) {
      const errorResponse = await err.response.json();

      return {
        ...pick(err, [`error`, `message`, `data`]),
        ...pick(errorResponse, [`status`]),
      };
    }
  }

  async getBlueprintDashboard({
    projectId,
    templateType,
    workspaceId,
  }: {
    projectId: ProjectId;
    workspaceId: number;
    templateType: string;
  }) {
    const url = `${this.buildEndpoint({workspaceId, projectId})}blueprints/`;
    const fetchOptions = this.getExtraFetchOptions();

    try {
      const result = await this.processResponse(
        await this.wrapErrorThrower(apiClient.get(url, {template_type: templateType}, fetchOptions)),
      );
      return result;
    } catch (err) {
      const errorResponse = await err.response.json();

      return {
        ...pick(err, [`error`, `message`, `data`]),
        ...pick(errorResponse, [`status`]),
      };
    }
  }

  async getBlueprintConfig({
    projectId,
    workspaceId,
    dashboardID,
  }: {
    projectId: ProjectId;
    workspaceId: number;
    dashboardID: number;
  }): Promise<BlueprintConfig> {
    const url = `${this.buildEndpoint({workspaceId, projectId, dashboardID})}blueprints-config/`;
    const fetchOptions = this.getExtraFetchOptions();

    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.get(url, null, fetchOptions)));
    return result.blueprint_config;
  }

  async updateBlueprintDashboard({
    projectId,
    workspaceId,
    dashboardId,
    cards,
  }: {
    projectId: ProjectId;
    workspaceId: number;
    dashboardId: any;
    cards: any;
  }) {
    const url = `${this.buildEndpoint({workspaceId, projectId})}blueprints-finish/`;
    const fetchOptions = this.getExtraFetchOptions();

    try {
      const result = await this.processResponse(
        await this.wrapErrorThrower(apiClient.patch(url, {dashboard_id: dashboardId, cards}, fetchOptions)),
      );
      return result;
    } catch (err) {
      const errorResponse = await err.response.json();

      return {
        ...pick(err, [`error`, `message`, `data`]),
        ...pick(errorResponse, [`status`]),
      };
    }
  }

  async commitBlueprintCohorts({
    projectId,
    workspaceId,
    cohorts,
  }: {
    projectId: ProjectId;
    workspaceId: number;
    cohorts: any;
  }) {
    const url = `${this.buildEndpoint({workspaceId, projectId})}blueprints-cohorts/`;
    const fetchOptions = this.getExtraFetchOptions();

    try {
      await apiClient.patch(url, {cohorts}, fetchOptions);
      return true;
    } catch (err) {
      const errorResponse = await err.response.json();

      return {
        ...pick(err, [`error`, `message`, `data`]),
        ...pick(errorResponse, [`status`]),
      };
    }
  }

  /** Sends a HTTP DELETE request to remove a dashboard */
  async deleteDashboard({
    projectId,
    dashboardID,
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(await this.wrapErrorThrower(apiClient.delete(url, fetchOptions)));
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends an HTTP PATCH request to restore a dashboard */
  async undeleteDashboard({
    dashboardId,
    projectId,
    workspaceId,
  }: {
    dashboardId: number;
    projectId: string | number;
    workspaceId?: number;
  }): Promise<Dashboard> {
    const url = this.buildEndpoint({projectId, dashboardID: dashboardId, workspaceId});
    const fetchOptions = this.getExtraFetchOptions();
    const result = await this.processResponse(
      await this.wrapErrorThrower(apiClient.patch(url, {undelete: true}, fetchOptions)),
    );
    await this.invalidateFetchCache();
    return result;
  }

  /** Sends a HTTP POST request to remove multiple dashboards */
  async deleteDashboards({
    projectId,
    dashboardIds,
    workspaceId,
  }: {
    projectId: ProjectId;
    dashboardIds: number[];
    workspaceId?: number;
  }): Promise<SuccessResponse<string>> {
    const url = `${this.buildEndpoint({projectId, workspaceId})}bulk-delete`;
    return this.processResponse(await apiClient.post(url, {dashboard_ids: dashboardIds}));
  }

  /** Marks dashboard as favorite */
  async favoriteDashboard({
    projectId,
    dashboardID,
    workspaceId,
    isFavorited,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    isFavorited: boolean;
  }) {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId, favorites: true});
    const result = await (isFavorited ? apiClient.post(url, {}) : apiClient.delete(url));
    await this.invalidateFetchCache();
    return result;
  }

  /** Pin dashboard to a project */
  async pinDashboard({
    projectId,
    dashboardID,
    workspaceId,
    isPinned,
  }: {
    projectId: ProjectId;
    dashboardID: number;
    workspaceId?: number;
    isPinned: boolean;
  }) {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId, pin: true});
    const result = await (isPinned ? apiClient.post(url, {}) : apiClient.delete(url));
    await this.invalidateFetchCache();
    return result;
  }

  async wrapErrorThrower(promise: Promise<any>) {
    try {
      return await promise;
    } catch (error) {
      if (error.name === `ResponseError`) {
        const errorInfo = await error.response.json();
        this.throwApiError(errorInfo);
      } else {
        throw error;
      }
    }
  }

  throwApiError(apiResponse) {
    switch (apiResponse.type) {
      case FieldStringTooLongError.type:
        throw new FieldStringTooLongError(
          apiResponse.error,
          apiResponse[`field_name`],
          apiResponse[`field_length`],
          apiResponse[`max_length`],
        );
      case PermissionDeniedError.type:
        throw new PermissionDeniedError(apiResponse.error);
      case NotImplementedError.type:
        throw new NotImplementedError(apiResponse.error);
      case InvalidParamsError.type:
        throw new InvalidParamsError(apiResponse.error);
      case DashboardDoesNotExistError.type:
        throw new DashboardDoesNotExistError(apiResponse.error);
      case CardDoesNotExistError.type:
        throw new CardDoesNotExistError(apiResponse.error);
      case CardOrderOutOfSyncError.type:
        throw new CardOrderOutOfSyncError(apiResponse.error);
      case BookmarkDoesNotExistError.type:
        throw new BookmarkDoesNotExistError(apiResponse.error);
    }
  }

  processResponse(response) {
    if (response.error) {
      this.throwApiError(response);
    }

    // TODO: untangle BaseClient and use apiClient directly
    // BaseClient uses apiClient.fetch which returns top level object
    // apiClient.(get|put|post|patch|delete) return the value of response.results
    // we should use move away from using apiClient.fetch directly unless the server response
    // is not conformant to the api response format
    if (Object.prototype.hasOwnProperty.call(response, `results`)) {
      return response.results;
    }

    return response;
  }

  async invalidateFetchCache(): Promise<void> {
    // this gets us out of trouble for now, but in the future Dashboards should migrate to Store
    await purgeFetchCachePathsMatchingRegex(/^\/api\/app\/(?:workspaces|projects)\/\d+\/dashboard.*/);
  }

  async updateReportLink({reportLinkId, projectId, workspaceId, dashboardID, updateData}) {
    const url = this.buildEndpoint({projectId, dashboardID, workspaceId, reportLinkId});
    await apiClient.patch(url, updateData);
  }

  //TODO @shayan or @gonzalo remove this and just use addNewContentToDashboard after GA
  async addNewDashboardBookmark({dashboardOrigin = null, bookmarkData, projectId, workspaceId, dashboardId}) {
    const contentType = ContentType.Report;

    const gridDetails = dashboardOrigin?.grid_details;

    const contentParams = {bookmark: {...omit(bookmarkData, `id`), dashboard_id: dashboardId}};

    const bookmark = await this.addNewContentToDashboard({
      gridDetails,
      projectId,
      workspaceId,
      dashboardId,
      contentDetails: {contentParams, contentType},
    });

    return bookmark;
  }

  async addNewContentToDashboard({
    gridDetails = null,
    contentDetails,
    dashboardId,
    projectId,
    workspaceId,
  }: {
    gridDetails?: any;
    contentDetails: ContentDetails;
    dashboardId: number;
    projectId: any;
    workspaceId: any;
  }) {
    const newCell = {cellId: `-1`, colSpan: MAX_CELL_COL_SPAN};

    let rowWithAddedCell;

    if (gridDetails) {
      const {row} = gridDetails;
      const updatedCellItems = addNewCellToRow(row.cellItems, newCell, 0);
      rowWithAddedCell = {...row, cellItems: updatedCellItems};
    }

    const fields = {
      ...(rowWithAddedCell ? {layout: getLayoutUpdateParams([rowWithAddedCell], gridDetails.rowsOrder)} : {}),
      content: {
        action: ContentActionType.Create,
        content_type: contentDetails.contentType,
        content_params: contentDetails.contentParams,
      },
    };

    const {new_content: newContent} = await this.updateDashboardFields({
      projectId,
      workspaceId,
      dashboardID: dashboardId,
      fields,
    });

    return newContent;
  }

  //TODO @shayan or @gonzalo remove this and just use deleteDashboardContent after GA
  async removeDashboardBookmark({bookmark, projectId, workspaceId}) {
    const dashboardId = bookmark?.dashboard_id || bookmark.dashboard?.id;
    const bookmarkId = bookmark.id;
    const loadDashboard = this.querySingle({projectId, workspaceId, dashboardID: dashboardId});
    const dashboard = await loadDashboard.network;

    const content = {contentId: bookmarkId, contentType: ContentType.Report};

    await this.deleteDashboardContent({gridLayout: dashboard.layout, content, projectId, workspaceId, dashboardId});
  }

  async deleteDashboardContent({gridLayout, content, projectId, workspaceId, dashboardId}) {
    const fields = {
      layout: getLayoutParamWithContentRemoved(content, gridLayout),
      content: {
        action: ContentActionType.Delete,
        content_type: content.contentType,
        content_id: content.contentId,
      },
    };

    const dashboard = await this.updateDashboardFields({
      projectId,
      workspaceId,
      dashboardID: dashboardId,
      fields,
    });
    return dashboard;
  }

  async nestDashboard({projectId, workspaceId, dashboard, parentDashboardId}) {
    // we are using "unmove"/"moveTo", so we set the new parent dashboard id into the URL
    const url = this.buildEndpoint({projectId, dashboardID: parentDashboardId, workspaceId});

    const fields = {
      content: {
        action: ContentActionType.UnMove,
        content_type: ContentType.Dashboard,
        content_id: dashboard.id,
      },
    };
    const result = await apiClient.patch<Dashboard>(url, fields);
    return result;
  }

  async unnestDashboard({projectId, workspaceId, dashboard, parentDashboardId}) {
    // we are using "move"/"moveFrom", so we set the old parent dashboard id into the URL
    const url = this.buildEndpoint({projectId, dashboardID: parentDashboardId, workspaceId});

    const fields = {
      content: {
        action: ContentActionType.Move,
        content_type: ContentType.Dashboard,
        content_id: dashboard.id,
        content_params: {
          target_dashboard_id: undefined,
        },
      },
    };
    const result = await apiClient.patch<Dashboard>(url, fields);
    return result;
  }
}
export type DashboardsClient = InstanceType<typeof DashClient>;
export const dashboardsClient = new DashClient();
